import { defineMessages, useIntl } from 'react-intl'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Heading from 'LuxKit/Typography/Heading'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import type { ModalFooterActionProps } from 'LuxKit/Modal/ModalFooter'
import Group from 'components/utils/Group'
import {
  INTL_DMY_CASUAL_FORMAT,
} from 'constants/dateFormats'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { formatCallbackTimeLabel } from '../RequestAgentCallbackUtils'
import type { MappedRequestAgentCallbackResult } from '../requestAgentCallbackTypes'

interface Props {
  customer: MappedRequestAgentCallbackResult['customer']
  onStartAnotherRequest?: () => void
}

const messages = defineMessages({
  title: {
    id: 'SOPFSrEA7bGWKcSXcAiI',
    defaultMessage: 'Callback request sent',
    description: 'Heading title for callback request confirmation modal',
  },
  agentWillCall: {
    id: 'b57U3JXB2e4sjq8WCgtm',
    defaultMessage: 'A customer service agent will call you at',
    description: 'Text preceding callback time and date details in a modal dialog',
  },
  callbackTime: {
    id: 'uhqiXU5epsdg3ZCJLsax',
    defaultMessage: 'Time: {callbackTime}',
    description: 'Callback time label with scheduled time value in a modal dialog',
  },
  callbackDate: {
    id: 'nxmeqSakwIpYv5Qsciyq',
    defaultMessage: 'Date: {callbackDate}',
    description: 'Callback date label with scheduled date value in a modal dialog',
  },
  confirmationEmailSent: {
    id: 'iVghxqNNfzm-7op-X6RR',
    defaultMessage: 'A confirmation email has been sent to {email}.',
    description: 'Confirmation message showing email address where callback confirmation was sent',
  },
  closeButton: {
    id: 'WBCU5gxBkiV7tdThi_tc',
    defaultMessage: 'Close',
    description: 'Close button for callback confirmation modal dialog window',
  },
  startAnotherRequest: {
    id: '9w0JP8XoxtCoMvQj-rin',
    defaultMessage: 'Start another request',
    description: 'Secondary action button in callback confirmation modal to immediately open a fresh callback request flow for the next customer',
  },
})

interface ContentProps {
  customer: MappedRequestAgentCallbackResult['customer']
  showTitle?: boolean
  agentWillCallMessage?: string
}

export function RequestAgentCallbackConfirmationContent(props: ContentProps) {
  const intl = useIntl()
  const intlDateFormatter = useIntlDateFormatter()
  const { callbackTime, callbackDate, email } = props.customer
  const formattedCallbackDate = callbackDate
    ? intlDateFormatter(callbackDate, INTL_DMY_CASUAL_FORMAT)
    : ''

  return (
    <VerticalSpacer gap={20}>
      {props.showTitle && (
        <Heading as="h2" variant="heading3">
          {intl.formatMessage(messages.title)}
        </Heading>
      )}
      <Group direction="vertical" gap={20}>
        <BodyTextBlock variant="large">
          {props.agentWillCallMessage ?? intl.formatMessage(messages.agentWillCall)}
        </BodyTextBlock>
        <Group direction="vertical" gap={0}>
          <BodyTextBlock variant="large" weight="bold">
            {intl.formatMessage(
              messages.callbackTime,
              { callbackTime: formatCallbackTimeLabel(callbackTime, intlDateFormatter) },
            )}
          </BodyTextBlock>
          <BodyTextBlock variant="large" weight="bold">
            {intl.formatMessage(
              messages.callbackDate,
              { callbackDate: formattedCallbackDate },
            )}
          </BodyTextBlock>
        </Group>
        <BodyTextBlock variant="large">
          {intl.formatMessage(messages.confirmationEmailSent, { email })}
        </BodyTextBlock>
      </Group>
    </VerticalSpacer>
  )
}

export function RequestAgentCallbackConfirmationModal(props: Props) {
  const intl = useIntl()
  const secondaryActionProps: ModalFooterActionProps | undefined = props.onStartAnotherRequest ? {
    children: intl.formatMessage(messages.startAnotherRequest),
    onClick: (_event, resolve) => {
      resolve()
      props.onStartAnotherRequest?.()
    },
  } : undefined

  return (
    <ModalBase>
      <form>
        <ModalHeader title={intl.formatMessage(messages.title)} />
        <ModalBody>
          <ModalContent>
            <RequestAgentCallbackConfirmationContent customer={props.customer} />
          </ModalContent>
        </ModalBody>
        <ModalFooter
          primaryActionProps={{
            children: intl.formatMessage(messages.closeButton),
          }}
          secondaryActionProps={secondaryActionProps}
        />
      </form>
    </ModalBase>
  )
}
